import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, Gift, Activity, Shield, MessageSquare, Loader, Trash2, Plus, Settings as SettingsIcon, Power, Edit2, X, Save, AlertTriangle, DollarSign, Check } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';
import api from '../api/axios';

const Admin = () => {
  const { user } = useAuth();
  const notify = useNotification();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [logs, setLogs] = useState([]);
  const [logsPage, setLogsPage] = useState(1);
  const [totalLogsPages, setTotalLogsPages] = useState(1);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [autoSites, setAutoSites] = useState({ stv1: [], stv2: [], stv3: [], shopii1: [], shopii2: [], shopii3: [], shopii4: [], skbased: [], ppkey: [] });
  const [newSites, setNewSites] = useState('');
  const [activePackage, setActivePackage] = useState('stv1');
  const [maintenance, setMaintenance] = useState({ is_active: false, message: '' });
  const [editingMessage, setEditingMessage] = useState(false);
  const [tempMessage, setTempMessage] = useState('');
  const [paymentTickets, setPaymentTickets] = useState([]);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [skConfig, setSkConfig] = useState({ sk_key: '', charge_type: 'cvv', currency: 'myr', amount: '4' });
  const [ppConfig, setPpConfig] = useState({ client_id: '', client_secret: '' });

  const tabs = [
    { id: 'dashboard', name: 'Dashboard', icon: Activity, roles: ['ADMIN', 'OWNER'] },
    { id: 'users', name: 'Users', icon: Users, roles: ['ADMIN', 'OWNER'] },
    { id: 'codes', name: 'Codes', icon: Gift, roles: ['ADMIN', 'OWNER'] },
    { id: 'payments', name: 'Payments', icon: DollarSign, roles: ['OWNER'] },
    { id: 'autosites', name: 'Auto Sites', icon: SettingsIcon, roles: ['OWNER'] },
    { id: 'logs', name: 'Logs', icon: Shield, roles: ['OWNER'] },
    { id: 'system', name: 'System', icon: Power, roles: ['OWNER'] },
  ];

  const filteredTabs = tabs.filter((tab) => tab.roles.includes(user?.status || ''));

  useEffect(() => {
    if (activeTab === 'dashboard') fetchStats();
    else if (activeTab === 'users') fetchUsers();
    else if (activeTab === 'logs') {
      setLogsPage(1); // Reset to page 1 when switching to logs tab
      fetchLogs(1);
    }
    else if (activeTab === 'autosites') fetchAutoSites();
    else if (activeTab === 'system') fetchMaintenance();
    else if (activeTab === 'payments') fetchPayments();
  }, [activeTab]);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/dashboard');
      if (response.data.success) setStats(response.data.stats);
    } catch (error) {
      console.error('Failed to fetch stats');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async (page = 1, search = '') => {
    setLoading(true);
    try {
      const response = await api.get(`/admin/users?page=${page}&limit=10&search=${search}`);
      if (response.data.success) {
        setUsers(response.data.users);
        setTotalUsers(response.data.total);
        setCurrentPage(page);
      }
    } catch (error) {
      console.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchUsers(1, searchQuery);
  };

  const handleEditUser = (user) => {
    setEditingUser({
      id: user.id,
      original: { // Store original values
        status: user.status,
        credit: user.credit,
        cash: user.cash,
        plan: user.plan,
        expiry: user.expiry
      },
      status: user.status,
      credit: user.credit,
      cash: user.cash,
      plan: user.plan,
      expiry: user.expiry
    });
  };

  const handleSaveUser = async () => {
    if (!editingUser) return;
    
    // Only include changed fields
    const changes = {};
    if (editingUser.status !== editingUser.original.status) changes.status = editingUser.status;
    if (editingUser.credit !== editingUser.original.credit) changes.credit = editingUser.credit;
    if (editingUser.cash !== editingUser.original.cash) changes.cash = editingUser.cash;
    if (editingUser.plan !== editingUser.original.plan) changes.plan = editingUser.plan;
    if (editingUser.expiry !== editingUser.original.expiry) changes.expiry = editingUser.expiry;
    
    if (Object.keys(changes).length === 0) {
      notify.info('No changes made');
      setEditingUser(null);
      return;
    }
    
    try {
      await api.put(`/admin/users/${editingUser.id}`, changes);
      notify.success(`User updated! ${Object.keys(changes).length} field(s) changed.`);
      setEditingUser(null);
      fetchUsers(currentPage, searchQuery);
    } catch (error) {
      notify.error('Failed to update user');
    }
  };


  const fetchPayments = async () => {
    setLoadingPayments(true);
    try {
      const response = await api.get('/admin/payments?status=all');
      if (response.data.success) {
        setPaymentTickets(response.data.tickets);
      }
    } catch (error) {
      notify.error('Failed to fetch payments');
    } finally {
      setLoadingPayments(false);
    }
  };

  const handleApprovePayment = async (ticketId) => {
    const confirmed = await notify.confirm(
      'This will add cash to the user\'s account. Are you sure?',
      'Approve Payment'
    );
    
    if (!confirmed) return;

    try {
      const response = await api.post('/admin/payment/approve', { ticket_id: ticketId });
      if (response.data.success) {
        notify.success(response.data.message, 5000);
        fetchPayments();
      }
    } catch (error) {
      notify.error(error.response?.data?.error || 'Failed to approve payment', 4000);
    }
  };

  const handleRejectPayment = async (ticketId) => {
    const confirmed = await notify.confirm(
      'Are you sure you want to reject this payment?',
      'Reject Payment'
    );
    
    if (!confirmed) return;

    try {
      const response = await api.post('/admin/payment/reject', { 
        ticket_id: ticketId,
        reason: 'Rejected by admin'
      });
      if (response.data.success) {
        notify.success('Payment rejected', 3000);
        fetchPayments();
      }
    } catch (error) {
      notify.error(error.response?.data?.error || 'Failed to reject payment', 4000);
    }
  };

  const fetchLogs = async (page = 1) => {
    setLoadingLogs(true);
    try {
      const response = await api.get(`/admin/logs?limit=10&page=${page}`);
      if (response.data.success) {
        setLogs(response.data.logs);
        setTotalLogsPages(response.data.totalPages || 1);
        setLogsPage(page);
      }
    } catch (error) {
      console.error('Failed to fetch logs');
      notify.error('Failed to load logs', 3000);
    } finally {
      setLoadingLogs(false);
    }
  };

  const fetchAutoSites = async () => {
    setLoading(true);
    try {
      const [v1Res, v2Res, v3Res, shopii1Res, shopii2Res, shopii3Res, shopii4Res, skKeysRes, ppKeysRes] = await Promise.all([
        api.get('/admin/auto-sites?package=stv1'),
        api.get('/admin/auto-sites?package=stv2'),
        api.get('/admin/auto-sites?package=stv3'),
        api.get('/admin/auto-sites?package=shopii1'),
        api.get('/admin/auto-sites?package=shopii2'),
        api.get('/admin/auto-sites?package=shopii3'),
        api.get('/admin/auto-sites?package=shopii4'),
        api.get('/admin/sk-keys'),
        api.get('/admin/pp-keys')
      ]);
      
      setAutoSites({
        stv1: v1Res.data.success ? v1Res.data.sites || [] : [],
        stv2: v2Res.data.success ? v2Res.data.sites || [] : [],
        stv3: v3Res.data.success ? v3Res.data.sites || [] : [],
        shopii1: shopii1Res.data.success ? shopii1Res.data.sites || [] : [],
        shopii2: shopii2Res.data.success ? shopii2Res.data.sites || [] : [],
        shopii3: shopii3Res.data.success ? shopii3Res.data.sites || [] : [],
        shopii4: shopii4Res.data.success ? shopii4Res.data.sites || [] : [],
        skbased: skKeysRes.data.success ? skKeysRes.data.configs || [] : [],
        ppkey: ppKeysRes.data.success ? ppKeysRes.data.configs || [] : []
      });
    } catch (error) {
      console.error('Failed to fetch auto sites');
    } finally {
      setLoading(false);
    }
  };

  const fetchMaintenance = async () => {
    setLoading(true);
    try {
      const response = await api.get('/maintenance/status');
      if (response.data.success) {
        setMaintenance({ 
          is_active: response.data.is_active, 
          message: response.data.message 
        });
        setTempMessage(response.data.message);
      }
    } catch (error) {
      console.error('Failed to fetch maintenance status');
    } finally {
      setLoading(false);
    }
  };

  const toggleMaintenance = async () => {
    const confirmMsg = maintenance.is_active 
      ? 'Disable maintenance mode? All users will be able to login.'
      : 'Enable maintenance mode? Only admins/owners can login.';
    
    const confirmed = await notify.confirm(confirmMsg, 'Maintenance Mode');
    if (!confirmed) return;

    try {
      const response = await api.post('/maintenance/toggle', {
        is_active: !maintenance.is_active,
        message: tempMessage
      });
      if (response.data.success) {
        setMaintenance({ is_active: !maintenance.is_active, message: tempMessage });
        setEditingMessage(false);
        notify.success(`Maintenance mode ${!maintenance.is_active ? 'enabled' : 'disabled'}!`);
      }
    } catch (error) {
      notify.error('Failed to toggle maintenance mode');
    }
  };

  const saveMaintenanceMessage = async () => {
    try {
      const response = await api.post('/maintenance/toggle', {
        is_active: maintenance.is_active,
        message: tempMessage
      });
      if (response.data.success) {
        setMaintenance({ ...maintenance, message: tempMessage });
        setEditingMessage(false);
        notify.success('Maintenance message updated!');
      }
    } catch (error) {
      notify.error('Failed to update message');
    }
  };

  const deleteUser = async (userId) => {
    const confirmed = await notify.confirm('Are you sure you want to delete this user?', 'Delete User');
    if (!confirmed) return;
    
    try {
      const response = await api.delete(`/admin/users/${userId}`);
      if (response.data.success) {
        notify.success('User deleted successfully!', 3000);
        fetchUsers();
      }
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Failed to delete user';
      notify.error(errorMsg, 4000);
      console.error('Delete user error:', error);
    }
  };

  const addAutoSites = async () => {
    if (!newSites.trim()) {
      notify.warning(activePackage === 'skbased' ? 'Please enter SK keys' : 'Please enter sites (one per line)');
      return;
    }

    const itemsArray = newSites.split('\n').map(s => s.trim()).filter(s => s);
    
    if (itemsArray.length === 0) {
      notify.warning('No valid items found');
      return;
    }

    if (itemsArray.length > 100) {
      notify.error('Maximum 100 items at once');
      return;
    }

    try {
      if (activePackage === 'skbased') {
        // Save SK keys to CONFIG_DATABASE
        await api.post('/admin/sk-keys/bulk', { sk_keys: itemsArray });
        notify.success(`${itemsArray.length} SK key(s) added as default!`);
      } else {
        // Save sites
        await api.post('/admin/auto-sites/bulk', { 
          sites: itemsArray,
          package: activePackage
        });
        notify.success(`${itemsArray.length} sites added to ${activePackage.toUpperCase()}!`);
      }
      setNewSites('');
      fetchAutoSites();
    } catch (error) {
      notify.error(error.response?.data?.error || 'Failed to add items');
    }
  };

  const deleteAutoSite = async (site, pkg) => {
    const confirmed = await notify.confirm(`Remove ${site} from ${pkg.toUpperCase()}?`, 'Delete Site');
    if (!confirmed) return;
    
    try {
      await api.delete(`/admin/auto-sites/${encodeURIComponent(site)}?package=${pkg}`);
      notify.success('Site removed!');
      fetchAutoSites();
    } catch (error) {
      notify.error('Failed to remove site');
    }
  };

  const deleteSKKey = async (keyId) => {
    const confirmed = await notify.confirm('Remove this SK key?', 'Delete Key');
    if (!confirmed) return;
    
    try {
      await api.delete(`/admin/sk-keys/${keyId}`);
      notify.success('SK key removed!');
      fetchAutoSites();
    } catch (error) {
      notify.error('Failed to remove SK key');
    }
  };

  const deletePPKey = async (keyId) => {
    const confirmed = await notify.confirm('Remove this PayPal credential?', 'Delete Credential');
    if (!confirmed) return;
    
    try {
      await api.delete(`/admin/pp-keys/${keyId}`);
      notify.success('PayPal credential removed!');
      fetchAutoSites();
    } catch (error) {
      notify.error('Failed to remove PayPal credential');
    }
  };

  const deleteAllSites = async (pkg) => {
    const confirmed = await notify.confirm(`Delete ALL ${pkg === 'skbased' ? 'SK keys' : 'sites'} from ${pkg.toUpperCase()}? This cannot be undone!`, 'Clear All');
    if (!confirmed) return;
    
    try {
      const response = await api.delete(`/admin/auto-sites/bulk?package=${pkg}`);
      if (response.data.success) {
        // Immediately update state to show empty
        setAutoSites(prev => ({
          ...prev,
          [pkg]: []
        }));
        notify.success(`All ${pkg === 'skbased' ? 'SK keys' : 'sites'} removed from ${pkg.toUpperCase()}!`);
        // Also fetch from server to confirm
        fetchAutoSites();
      }
    } catch (error) {
      notify.error('Failed to remove items');
    }
  };

  const generateCode = async () => {
    // This still uses prompt for input - can be improved later
    const type = prompt('Enter type (PLAN1/PLAN2/PLAN3/FREE):');
    if (!type) return;
    const count = prompt('How many codes?', '1');
    if (!count) return;

    try {
      await api.post('/admin/redeem/generate', { type: type.toUpperCase(), count: parseInt(count) });
      notify.success(`${count} codes generated successfully!`);
    } catch (error) {
      notify.error('Failed to generate codes');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a1628] via-[#0d1b2a] to-[#0a1628] pb-24">
      <div className="max-w-4xl mx-auto px-4 py-6">
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <Shield className="text-purple-400" size={24} />
            <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-purple-400 to-pink-500 bg-clip-text text-transparent">Admin Panel</h1>
          </div>
          <p className="text-gray-400 text-sm">Manage your CyborX system</p>
        </motion.div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2 scrollbar-hide">
          {filteredTabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <motion.button
                key={tab.id}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-xl whitespace-nowrap transition-all border text-xs sm:text-sm ${activeTab === tab.id ? 'bg-gradient-to-r from-purple-500/20 to-pink-500/20 border-purple-400/30 text-purple-300' : 'bg-white/5 border-white/5 text-gray-400 hover:bg-white/10'}`}
              >
                <Icon size={14} className="sm:w-4 sm:h-4" />
                <span className="font-medium">{tab.name}</span>
              </motion.button>
            );
          })}
        </div>

        {loading && (
          <div className="flex justify-center py-12">
            <Loader className="animate-spin text-purple-400" size={48} />
          </div>
        )}

        {/* Dashboard Tab */}
        {!loading && activeTab === 'dashboard' && stats && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
              <StatsCard title="Total Users" value={stats.users.total} color="from-cyan-400 to-blue-500" />
              <StatsCard title="Premium" value={stats.users.premium} color="from-yellow-400 to-orange-500" />
              <StatsCard title="Revenue" value={`$${stats.transactions.revenue || 0}`} color="from-green-400 to-emerald-500" />
              <StatsCard title="Codes" value={stats.codes.active} color="from-purple-400 to-pink-500" />
            </div>
          </motion.div>
        )}

        {/* Users Tab */}
        {!loading && activeTab === 'users' && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
            {/* Search Bar */}
            <div className="flex gap-2">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Search by name, username, or ID..."
                className="flex-1 bg-gray-900/50 border border-gray-700/50 text-gray-300 placeholder-gray-500 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-cyan-500/50"
              />
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleSearch}
                className="px-4 py-2.5 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg font-semibold text-sm"
              >
                Search
              </motion.button>
            </div>

            {/* Users List */}
            <div className="space-y-3">
              {users.map((u, index) => (
                <motion.div key={u.id} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: index * 0.05 }} className="bg-gradient-to-br from-gray-800/40 to-gray-900/40 backdrop-blur-xl rounded-xl p-4 border border-gray-700/50">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-white font-bold truncate">{u.first_name && u.last_name ? `${u.first_name} ${u.last_name}` : u.username}</p>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${u.status === 'OWNER' ? 'bg-purple-500/20 text-purple-400' : u.status === 'ADMIN' ? 'bg-cyan-500/20 text-cyan-400' : u.status === 'PREMIUM' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-gray-700/50 text-gray-400'}`}>
                          {u.status}
                        </span>
                      </div>
                      <p className="text-gray-400 text-xs">ID: {u.id}</p>
                      <p className="text-gray-500 text-xs">@{u.username}</p>
                      <div className="flex flex-wrap gap-2 mt-2 text-xs">
                        <span className="text-purple-400">Credits: {u.credit}</span>
                        <span className="text-gray-500">•</span>
                        <span className="text-green-400">Cash: ${parseFloat(u.cash || 0).toFixed(2)}</span>
                        <span className="text-gray-500">•</span>
                        <span className="text-orange-400">Plan: {u.plan}</span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => handleEditUser(u)}
                        className="p-2 bg-cyan-600/20 hover:bg-cyan-600/30 border border-cyan-500/50 rounded-lg text-cyan-400"
                      >
                        <Edit2 size={16} />
                      </motion.button>
                      {u.status !== 'OWNER' && (
                        <button
                          onClick={() => deleteUser(u.id)}
                          className="p-2 text-red-400 hover:text-red-300 transition-colors"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Pagination */}
            {totalUsers > 10 && (
              <div className="flex items-center justify-between mt-4">
                <p className="text-gray-400 text-sm">
                  Showing {((currentPage - 1) * 10) + 1}-{Math.min(currentPage * 10, totalUsers)} of {totalUsers}
                </p>
                <div className="flex gap-2">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => fetchUsers(currentPage - 1, searchQuery)}
                    disabled={currentPage === 1}
                    className="px-3 py-1.5 bg-gray-700/50 hover:bg-gray-600/50 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-sm"
                  >
                    Previous
                  </motion.button>
                  <span className="px-3 py-1.5 bg-cyan-500/20 text-cyan-400 rounded-lg text-sm">
                    Page {currentPage} of {Math.ceil(totalUsers / 10)}
                  </span>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => fetchUsers(currentPage + 1, searchQuery)}
                    disabled={currentPage >= Math.ceil(totalUsers / 10)}
                    className="px-3 py-1.5 bg-gray-700/50 hover:bg-gray-600/50 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-sm"
                  >
                    Next
                  </motion.button>
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* Edit User Modal */}
        <AnimatePresence>
          {editingUser && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[10001]"
                onClick={() => setEditingUser(null)}
              />
              <div className="fixed inset-0 flex items-center justify-center z-[10002] p-4">
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className="w-full max-w-md bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-2xl border border-gray-700/50 shadow-2xl p-6"
                >
                  <h3 className="text-xl font-bold text-white mb-4">Edit User</h3>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Status</label>
                      <select
                        value={editingUser.status}
                        onChange={(e) => setEditingUser({...editingUser, status: e.target.value})}
                        className="w-full bg-gray-900/50 border border-gray-700/50 text-gray-300 rounded-lg px-3 py-2 text-sm"
                      >
                        <option value="FREE">FREE</option>
                        <option value="PREMIUM">PREMIUM</option>
                        <option value="ADMIN">ADMIN</option>
                        <option value="OWNER">OWNER</option>
                      </select>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">Credits</label>
                        <input
                          type="number"
                          value={editingUser.credit}
                          onChange={(e) => setEditingUser({...editingUser, credit: e.target.value})}
                          className="w-full bg-gray-900/50 border border-gray-700/50 text-gray-300 rounded-lg px-3 py-2 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">Cash</label>
                        <input
                          type="number"
                          step="0.01"
                          value={editingUser.cash}
                          onChange={(e) => setEditingUser({...editingUser, cash: e.target.value})}
                          className="w-full bg-gray-900/50 border border-gray-700/50 text-gray-300 rounded-lg px-3 py-2 text-sm"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Plan</label>
                      <input
                        type="text"
                        value={editingUser.plan}
                        onChange={(e) => setEditingUser({...editingUser, plan: e.target.value})}
                        className="w-full bg-gray-900/50 border border-gray-700/50 text-gray-300 rounded-lg px-3 py-2 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Expiry (DD-MM-YYYY)</label>
                      <input
                        type="text"
                        value={editingUser.expiry}
                        onChange={(e) => setEditingUser({...editingUser, expiry: e.target.value})}
                        placeholder="31-12-2025"
                        className="w-full bg-gray-900/50 border border-gray-700/50 text-gray-300 rounded-lg px-3 py-2 text-sm"
                      />
                    </div>
                  </div>
                  <div className="flex gap-3 mt-6">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setEditingUser(null)}
                      className="flex-1 py-2.5 bg-gray-700/50 hover:bg-gray-600/50 text-white rounded-xl font-semibold"
                    >
                      Cancel
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={handleSaveUser}
                      className="flex-1 py-2.5 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white rounded-xl font-semibold"
                    >
                      Save
                    </motion.button>
                  </div>
                </motion.div>
              </div>
            </>
          )}
        </AnimatePresence>

        {/* Auto Sites Tab */}
        {!loading && activeTab === 'autosites' && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
            <div className="bg-gradient-to-br from-gray-800/40 to-gray-900/40 backdrop-blur-xl rounded-2xl p-5 border border-gray-700/50">
              <h3 className="text-lg font-bold text-white mb-4">Auto Site Packages</h3>
              
              {/* Package Selector */}
              <div className="grid grid-cols-4 sm:grid-cols-8 gap-2 mb-4">
                {['stv1', 'stv2', 'stv3', 'shopii1', 'shopii2', 'shopii3', 'shopii4', 'skbased', 'ppkey'].map((pkg) => (
                  <motion.button
                    key={pkg}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setActivePackage(pkg)}
                    className={`px-2 py-2 rounded-lg font-semibold text-xs whitespace-nowrap ${
                      activePackage === pkg
                        ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white'
                        : 'bg-gray-800/50 text-gray-400 border border-gray-700/50'
                    }`}
                  >
                    {pkg === 'skbased' ? 'SK BASED' : pkg === 'ppkey' ? 'PP KEY' : pkg.toUpperCase()}
                  </motion.button>
                ))}
              </div>

              {/* Package Info */}
              <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg mb-4">
                <p className="text-blue-300 text-xs font-semibold mb-1">
                  {activePackage === 'stv1' && 'Stripe Auth V1 - Port 1011'}
                  {activePackage === 'stv2' && 'Stripe Auth V2 - Port 1012'}
                  {activePackage === 'stv3' && 'Stripe Auth V3 - Port 1013'}
                  {activePackage === 'shopii1' && 'Shopii #1 (A1.0$) - Premium Only'}
                  {activePackage === 'shopii2' && 'Shopii #2 (A5.0$)'}
                  {activePackage === 'shopii3' && 'Shopii #3 (A10.0$)'}
                  {activePackage === 'shopii4' && 'Shopii #4 (A50.0$)'}
                  {activePackage === 'skbased' && 'SK BASED CVV (A1.0$) - Default Keys'}
                  {activePackage === 'ppkey' && 'PP KeyBased (A1.0$) - Default Credentials'}
                </p>
                <p className="text-gray-400 text-xs break-all">
                  {activePackage === 'skbased' 
                    ? 'https://api.savvyapi.dev/'
                    : activePackage === 'ppkey'
                    ? 'http://206.206.78.217:1355/'
                    : activePackage.startsWith('shopii') 
                      ? 'https://cyborxchecker.com/api/autog.php'
                      : `http://206.206.78.217:${activePackage === 'stv1' ? '1011' : activePackage === 'stv2' ? '1012' : '1013'}/`
                  }
                </p>
              </div>

              {/* Bulk Add Sites or SK Config or PP Config */}
              {activePackage !== 'skbased' && activePackage !== 'ppkey' ? (
                <div className="mb-4">
                  <label className="block text-sm text-gray-400 mb-2">Add Sites (One per line, max 100)</label>
                  <textarea
                    value={newSites}
                    onChange={(e) => setNewSites(e.target.value)}
                    placeholder="shop-example-com\nstore-demo-net\nshop-test-com"
                    className="w-full h-32 bg-gray-900/50 border border-gray-700/50 text-gray-300 placeholder-gray-500 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-cyan-500/50 resize-none font-mono"
                  />
                  <p className="text-gray-500 text-xs mt-1">
                    {newSites.split('\n').filter(s => s.trim()).length} sites ready to add
                  </p>
                </div>
              ) : activePackage === 'skbased' ? (
                <div className="mb-4 space-y-3">
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Default SK Key</label>
                    <input
                      type="text"
                      value={skConfig.sk_key}
                      onChange={(e) => setSkConfig({...skConfig, sk_key: e.target.value})}
                      placeholder="sk_live_51..."
                      className="w-full bg-gray-900/50 border border-gray-700/50 text-gray-300 placeholder-gray-500 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-cyan-500/50 font-mono"
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block text-sm text-gray-400 mb-2">Charge Type</label>
                      <select
                        value={skConfig.charge_type}
                        onChange={(e) => setSkConfig({...skConfig, charge_type: e.target.value})}
                        className="w-full bg-gray-900/50 border border-gray-700/50 text-gray-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-cyan-500/50"
                      >
                        <option value="cvv">CVV</option>
                        <option value="ccn">CCN</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm text-gray-400 mb-2">Currency</label>
                      <select
                        value={skConfig.currency}
                        onChange={(e) => setSkConfig({...skConfig, currency: e.target.value})}
                        className="w-full bg-gray-900/50 border border-gray-700/50 text-gray-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-cyan-500/50"
                      >
                        <option value="myr">MYR</option>
                        <option value="usd">USD</option>
                        <option value="eur">EUR</option>
                        <option value="gbp">GBP</option>
                        <option value="aud">AUD</option>
                        <option value="cad">CAD</option>
                        <option value="sgd">SGD</option>
                        <option value="inr">INR</option>
                        <option value="jpy">JPY</option>
                        <option value="hkd">HKD</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm text-gray-400 mb-2">Amount</label>
                      <select
                        value={skConfig.amount}
                        onChange={(e) => setSkConfig({...skConfig, amount: e.target.value})}
                        className="w-full bg-gray-900/50 border border-gray-700/50 text-gray-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-cyan-500/50"
                      >
                        {[1,2,3,4,5,6,7,8,9,10].map(n => (
                          <option key={n} value={n}>{n}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              ) : activePackage === 'ppkey' ? (
                <div className="mb-4 space-y-3">
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">PayPal Client ID</label>
                    <input
                      type="text"
                      value={ppConfig.client_id}
                      onChange={(e) => setPpConfig({...ppConfig, client_id: e.target.value})}
                      placeholder="Enter PayPal Client ID"
                      className="w-full bg-gray-900/50 border border-gray-700/50 text-gray-300 placeholder-gray-500 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-cyan-500/50 font-mono"
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-gray-400 mb-2">PayPal Client Secret</label>
                    <input
                      type="password"
                      value={ppConfig.client_secret}
                      onChange={(e) => setPpConfig({...ppConfig, client_secret: e.target.value})}
                      placeholder="Enter PayPal Client Secret"
                      className="w-full bg-gray-900/50 border border-gray-700/50 text-gray-300 placeholder-gray-500 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-cyan-500/50 font-mono"
                    />
                  </div>
                </div>
              ) : null}

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={async () => {
                  if (activePackage === 'skbased') {
                    if (!skConfig.sk_key.trim()) {
                      notify.warning('Please enter SK key');
                      return;
                    }
                    try {
                      await api.post('/admin/sk-keys/add-default', {
                        sk_key: skConfig.sk_key.trim(),
                        charge_type: skConfig.charge_type,
                        currency: skConfig.currency,
                        amount: parseInt(skConfig.amount)
                      });
                      notify.success('Default SK configuration saved!');
                      setSkConfig({ sk_key: '', charge_type: 'cvv', currency: 'myr', amount: '4' });
                      fetchAutoSites(); // Refresh the list
                    } catch (error) {
                      notify.error(error.response?.data?.error || 'Failed to save SK config');
                    }
                  } else if (activePackage === 'ppkey') {
                    if (!ppConfig.client_id.trim() || !ppConfig.client_secret.trim()) {
                      notify.warning('Please enter both Client ID and Client Secret');
                      return;
                    }
                    try {
                      await api.post('/admin/pp-keys/add-default', {
                        client_id: ppConfig.client_id.trim(),
                        client_secret: ppConfig.client_secret.trim()
                      });
                      notify.success('Default PayPal credentials saved!');
                      setPpConfig({ client_id: '', client_secret: '' });
                      fetchAutoSites(); // Refresh the list
                    } catch (error) {
                      notify.error(error.response?.data?.error || 'Failed to save PayPal config');
                    }
                  } else {
                    addAutoSites();
                  }
                }}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white rounded-lg font-semibold"
              >
                <Plus size={18} />
                {activePackage === 'skbased' ? 'Save Default SK Config' : activePackage === 'ppkey' ? 'Save Default PP Credentials' : `Add ${newSites.split('\n').filter(s => s.trim()).length} Sites to ${activePackage.toUpperCase()}`}
              </motion.button>

              {/* Sites List */}
              <div className="mt-6">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-white">
                    {activePackage.toUpperCase()} Sites ({autoSites[activePackage]?.length || 0})
                  </h4>
                  {autoSites[activePackage]?.length > 0 && (
                    <button
                      onClick={() => deleteAllSites(activePackage)}
                      className="text-xs text-red-400 hover:text-red-300"
                    >
                      Clear All
                    </button>
                  )}
                </div>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {autoSites[activePackage]?.length === 0 ? (
                    <p className="text-center text-gray-500 py-8 text-sm">
                      {activePackage === 'skbased' ? 'No SK keys configured yet' : activePackage === 'ppkey' ? 'No PP credentials configured yet' : 'No sites configured yet'}
                    </p>
                  ) : (
                    autoSites[activePackage]?.map((item, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.02 }}
                        className="flex items-center justify-between p-3 bg-gray-900/50 rounded-lg border border-gray-700/50 hover:border-cyan-500/30 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-2 h-2 rounded-full bg-green-500"></div>
                          {activePackage === 'skbased' ? (
                            <span className="text-white font-mono text-xs">{item.sk_key_masked}</span>
                          ) : activePackage === 'ppkey' ? (
                            <span className="text-white font-mono text-xs">{item.client_id_masked}</span>
                          ) : (
                            <span className="text-white font-mono text-xs">{item}</span>
                          )}
                        </div>
                        <button
                          onClick={() => activePackage === 'skbased' ? deleteSKKey(item._id) : activePackage === 'ppkey' ? deletePPKey(item._id) : deleteAutoSite(item, activePackage)}
                          className="text-red-400 hover:text-red-300 transition-colors"
                        >
                          <Trash2 size={16} />
                        </button>
                      </motion.div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Codes Tab */}
        {!loading && activeTab === 'codes' && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-gradient-to-br from-gray-800/40 to-gray-900/40 backdrop-blur-xl rounded-2xl p-5 border border-gray-700/50">
            <h3 className="text-lg font-bold text-white mb-4">Redeem Codes</h3>
            <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} onClick={generateCode} className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white rounded-lg font-semibold">
              <Plus size={18} />
              Generate Codes
            </motion.button>
          </motion.div>
        )}


        {/* Payments Tab */}
        {!loading && activeTab === 'payments' && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
            <div className="bg-gradient-to-br from-gray-800/40 to-gray-900/40 backdrop-blur-xl rounded-2xl p-6 border border-gray-700/50">
              <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <DollarSign size={24} className="text-green-400" />
                Payment Tickets
              </h3>

              {loadingPayments ? (
                <div className="text-center py-12">
                  <Loader className="animate-spin mx-auto text-cyan-400 mb-2" size={32} />
                  <p className="text-gray-400">Loading payments...</p>
                </div>
              ) : paymentTickets.length === 0 ? (
                <div className="text-center py-12">
                  <DollarSign size={48} className="text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-500">No payment tickets yet</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-[600px] overflow-y-auto">
                  {paymentTickets.map((ticket) => (
                    <div
                      key={ticket.ticket_id}
                      className={`p-5 rounded-xl border ${
                        ticket.status === 'approved'
                          ? 'bg-green-900/20 border-green-500/30'
                          : ticket.status === 'pending'
                          ? 'bg-yellow-900/20 border-yellow-500/30'
                          : ticket.status === 'expired'
                          ? 'bg-gray-900/20 border-gray-500/30'
                          : 'bg-red-900/20 border-red-500/30'
                      }`}
                    >
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <p className="text-white font-bold text-lg">${ticket.amount}</p>
                          <p className="text-gray-400 text-sm">{ticket.username} (ID: {ticket.user_id})</p>
                          <p className="text-gray-500 text-xs mt-1">{ticket.ticket_id}</p>
                        </div>
                        <div className="text-right">
                          <span
                            className={`px-3 py-1 rounded-full text-xs font-semibold ${
                              ticket.status === 'approved'
                                ? 'bg-green-500/20 text-green-400'
                                : ticket.status === 'pending'
                                ? 'bg-yellow-500/20 text-yellow-400'
                                : ticket.status === 'expired'
                                ? 'bg-gray-500/20 text-gray-400'
                                : 'bg-red-500/20 text-red-400'
                            }`}
                          >
                            {ticket.status.toUpperCase()}
                          </span>
                          {ticket.plan_name && (
                            <p className="text-cyan-400 text-xs mt-1">{ticket.plan_name}</p>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-2 text-xs text-gray-500 mb-3">
                        <span>{new Date(ticket.created_at).toLocaleString()}</span>
                        {ticket.status === 'approved' && ticket.approved_at && (
                          <span>• Approved: {new Date(ticket.approved_at).toLocaleString()}</span>
                        )}
                      </div>

                      {ticket.status === 'pending' && (
                        <div className="flex gap-2">
                          <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => handleApprovePayment(ticket.ticket_id)}
                            className="flex-1 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg font-semibold flex items-center justify-center gap-2"
                          >
                            <Check size={16} />
                            Approve
                          </motion.button>
                          <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => handleRejectPayment(ticket.ticket_id)}
                            className="flex-1 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg font-semibold flex items-center justify-center gap-2"
                          >
                            <X size={16} />
                            Reject
                          </motion.button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* System Settings Tab (OWNER ONLY) */}
        {!loading && activeTab === 'system' && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
            {/* Maintenance Mode Card */}
            <div className="bg-gradient-to-br from-gray-800/40 to-gray-900/40 backdrop-blur-xl rounded-2xl p-6 border border-gray-700/50">
              <div className="flex items-center gap-3 mb-6">
                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${
                  maintenance.is_active 
                    ? 'bg-gradient-to-br from-orange-500 to-red-500'
                    : 'bg-gradient-to-br from-gray-700 to-gray-800'
                }`}>
                  <Power size={24} className="text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-white">Maintenance Mode</h3>
                  <p className="text-gray-400 text-sm">
                    {maintenance.is_active ? 'System in maintenance' : 'System operational'}
                  </p>
                </div>
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  onClick={toggleMaintenance}
                  className={`relative w-16 h-8 rounded-full transition-colors ${
                    maintenance.is_active ? 'bg-orange-500' : 'bg-gray-600'
                  }`}
                >
                  <motion.div
                    className="absolute top-1 w-6 h-6 bg-white rounded-full shadow-lg"
                    animate={{ x: maintenance.is_active ? 36 : 4 }}
                    transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                  />
                </motion.button>
              </div>

              {maintenance.is_active && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="bg-orange-500/10 border border-orange-500/20 rounded-xl p-4 mb-4"
                >
                  <div className="flex items-start gap-3">
                    <AlertTriangle size={20} className="text-orange-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-orange-300 font-semibold text-sm mb-1">Maintenance Active</p>
                      <p className="text-gray-400 text-xs">Only admins and owners can login</p>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Message Editor */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-semibold text-gray-300">Custom Message</label>
                  {!editingMessage ? (
                    <button onClick={() => setEditingMessage(true)} className="text-cyan-400 hover:text-cyan-300 text-sm flex items-center gap-1">
                      <Edit2 size={14} />
                      Edit
                    </button>
                  ) : (
                    <div className="flex gap-2">
                      <button onClick={saveMaintenanceMessage} className="text-green-400 hover:text-green-300 text-sm flex items-center gap-1">
                        <Save size={14} />
                        Save
                      </button>
                      <button onClick={() => { setEditingMessage(false); setTempMessage(maintenance.message); }} className="text-gray-400 hover:text-gray-300 text-sm">
                        <X size={14} />
                      </button>
                    </div>
                  )}
                </div>
                {editingMessage ? (
                  <textarea value={tempMessage} onChange={(e) => setTempMessage(e.target.value)} className="w-full bg-gray-900/50 border border-gray-700/50 text-gray-300 placeholder-gray-500 rounded-lg px-4 py-3 text-sm h-32 resize-none focus:outline-none focus:border-cyan-500/50" />
                ) : (
                  <div className="bg-gray-900/50 border border-gray-700/50 rounded-lg px-4 py-3 text-gray-400 text-sm min-h-[80px]">
                    {maintenance.message || 'No custom message set'}
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="mt-4 p-4 bg-purple-500/10 border border-purple-500/20 rounded-xl">
                <p className="text-purple-300 text-xs font-semibold mb-2">How it works:</p>
                <ul className="text-gray-400 text-xs space-y-1">
                  <li>• When enabled, only Owner & Admin can login</li>
                  <li>• Other users see maintenance screen</li>
                  <li>• Telegram bot shows maintenance notice</li>
                  <li>• Custom message displayed to users</li>
                </ul>
              </div>
            </div>
          </motion.div>
        )}

        {/* Logs Tab */}
        {activeTab === 'logs' && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
            {loadingLogs ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader className="animate-spin text-cyan-400 mb-3" size={40} />
                <p className="text-gray-400 text-sm">Loading activity logs...</p>
              </div>
            ) : logs.length === 0 ? (
              <div className="text-center py-12">
                <Shield className="mx-auto text-gray-600 mb-3" size={48} />
                <p className="text-gray-500">No activity logs yet</p>
              </div>
            ) : (
              <>
                <div className="space-y-3">
                  {logs.map((log, index) => (
                    <motion.div key={log._id || index} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: index * 0.05 }} className="p-4 bg-gray-900/50 rounded-lg border border-gray-700/50 space-y-3">
                      <div className="flex justify-between items-start">
                        <div className="flex items-center gap-2">
                          <span className="text-cyan-400 font-semibold text-sm">{log.action_type}</span>
                          {log.admin && (
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              log.admin.status === 'OWNER' ? 'bg-purple-500/20 text-purple-400' :
                              log.admin.status === 'ADMIN' ? 'bg-blue-500/20 text-blue-400' :
                              'bg-gray-500/20 text-gray-400'
                            }`}>
                              {log.admin.status}
                            </span>
                          )}
                        </div>
                        <span className="text-gray-500 text-xs">{new Date(log.created_at).toLocaleString()}</span>
                      </div>
                      
                      <p className="text-gray-400 text-sm">{log.description}</p>
                      
                      <div className="flex flex-wrap gap-4 pt-2 border-t border-gray-700/30">
                        {log.admin && (
                          <div className="flex items-center gap-2">
                            <span className="text-gray-500 text-xs">Action By:</span>
                            <div className="flex flex-col">
                              <span className="text-white text-xs font-semibold">{log.admin.name}</span>
                              <span className="text-gray-500 text-xs">ID: {log.admin.id}</span>
                            </div>
                          </div>
                        )}
                        
                        {log.target_user && (
                          <div className="flex items-center gap-2">
                            <span className="text-gray-500 text-xs">Target User:</span>
                            <div className="flex flex-col">
                              <span className="text-orange-400 text-xs font-semibold">{log.target_user.name}</span>
                              <span className="text-gray-500 text-xs">ID: {log.target_user.id}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* Pagination Controls */}
                {totalLogsPages > 1 && (
                  <div className="flex items-center justify-center gap-2 pt-4">
                    <button
                      onClick={() => fetchLogs(logsPage - 1)}
                      disabled={logsPage === 1}
                      className={`px-4 py-2 rounded-lg border transition-colors ${
                        logsPage === 1
                          ? 'bg-gray-800/50 border-gray-700/50 text-gray-600 cursor-not-allowed'
                          : 'bg-gray-800/80 border-gray-700 text-white hover:bg-gray-700/80'
                      }`}
                    >
                      Previous
                    </button>
                    
                    <div className="flex items-center gap-1">
                      {[...Array(totalLogsPages)].map((_, i) => {
                        const pageNum = i + 1;
                        // Show first 3, last 3, and current page +/- 1
                        const showPage = pageNum <= 3 || 
                                        pageNum > totalLogsPages - 3 || 
                                        Math.abs(pageNum - logsPage) <= 1;
                        
                        const showEllipsis = (pageNum === 4 && logsPage > 5) || 
                                            (pageNum === totalLogsPages - 3 && logsPage < totalLogsPages - 4);
                        
                        if (showEllipsis) {
                          return <span key={i} className="text-gray-500 px-2">...</span>;
                        }
                        
                        if (!showPage) return null;
                        
                        return (
                          <button
                            key={i}
                            onClick={() => fetchLogs(pageNum)}
                            className={`w-10 h-10 rounded-lg border transition-colors ${
                              logsPage === pageNum
                                ? 'bg-cyan-500/20 border-cyan-500 text-cyan-400 font-bold'
                                : 'bg-gray-800/80 border-gray-700 text-gray-400 hover:bg-gray-700/80'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>
                    
                    <button
                      onClick={() => fetchLogs(logsPage + 1)}
                      disabled={logsPage === totalLogsPages}
                      className={`px-4 py-2 rounded-lg border transition-colors ${
                        logsPage === totalLogsPages
                          ? 'bg-gray-800/50 border-gray-700/50 text-gray-600 cursor-not-allowed'
                          : 'bg-gray-800/80 border-gray-700 text-white hover:bg-gray-700/80'
                      }`}
                    >
                      Next
                    </button>
                  </div>
                )}
                
                <div className="text-center text-gray-500 text-xs">
                  Page {logsPage} of {totalLogsPages}
                </div>
              </>
            )}
          </motion.div>
        )}
      </div>
    </div>
  );
};

const StatsCard = ({ title, value, color }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.9 }}
    animate={{ opacity: 1, scale: 1 }}
    whileHover={{ scale: 1.05, y: -5 }}
    className="bg-gradient-to-br from-gray-800/40 to-gray-900/40 backdrop-blur-md rounded-xl p-4 border border-gray-700/50"
  >
    <p className="text-gray-400 text-xs mb-2">{title}</p>
    <p className={`text-3xl font-bold bg-gradient-to-r ${color} bg-clip-text text-transparent`}>{value}</p>
  </motion.div>
);

export default Admin;
